// @see: https://github.com/johan44co/apple-pay-button
import React = require('react');
import { useEffect, useRef, useState } from 'react';

type ButtonType =
  | 'plain'
  | 'add-money'
  | 'book'
  | 'buy'
  | 'check-out'
  | 'continue'
  | 'contribute'
  | 'donate'
  | 'order'
  | 'pay'
  | 'reload'
  | 'rent'
  | 'set-up'
  | 'subscribe'
  | 'support'
  | 'tip'
  | 'top-up';

export interface ApplePayButtonProps {
  buttonStyle?: 'black' | 'white' | 'white-outline';
  type?: ButtonType;
  locale?: string;
  onClick?: () => void;
  className?: string;
  style?: {
    width?: string;
    height?: string;
    borderRadius?: string;
    padding?: string;
    boxSizing?: string;
  };
  disabled?: boolean;
}

const ApplePayButton: React.FC<ApplePayButtonProps> = ({
                                                         buttonStyle = 'black',
                                                         type = 'buy',
                                                         locale = 'en-GB',
                                                         onClick,
                                                         className,
                                                         style,
                                                         disabled
                                                       }) => {
  const buttonRef = useRef<HTMLElement | null>(null);
  const [showApplePayButton, setShowApplePayButton] = useState(false);

  const handleClick = (): void => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  useEffect(() => {
    const scriptId = 'apple-pay-sdk-script';
    const applePayScriptSrc = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';

    setShowApplePayButton(
      typeof ApplePaySession !== 'undefined' &&
      ApplePaySession.canMakePayments()
    );

    if (document.getElementById(scriptId)) return;

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = applePayScriptSrc;
    script.async = true;
    script.addEventListener('load', () => {
      setShowApplePayButton(
        typeof ApplePaySession !== 'undefined' &&
        ApplePaySession.canMakePayments()
      );
    });

    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    let internalButton: HTMLButtonElement | null = null;

    if (buttonRef.current?.shadowRoot) {
      internalButton = buttonRef.current.shadowRoot.querySelector('button');
      if (internalButton) {
        internalButton.addEventListener('click', handleClick);
      }
    }

    return () => {
      if (internalButton) {
        internalButton.removeEventListener('click', handleClick);
      }
    };
  }, [onClick, disabled]);

  const updateButtonStyle = (
    button?: Element | null,
    disabled?: boolean
  ): void => {
    const cursor = disabled ? 'not-allowed' : 'pointer';
    const opacity = disabled ? '0.5' : '1';
    button?.setAttribute('style', `cursor: ${cursor}; opacity: ${opacity};`);
  };

  useEffect(() => {
    if (buttonRef.current?.shadowRoot) {
      const button = buttonRef.current.shadowRoot.querySelector('button');
      updateButtonStyle(button, disabled);
    }
  }, [disabled, buttonRef.current?.shadowRoot]);

  const createApplePayButtonStyle = (style?: ApplePayButtonProps['style']): string => `
    apple-pay-button {
        --apple-pay-button-width: ${style?.width ?? 'auto'};
        --apple-pay-button-height: ${style?.height ?? '30px'};
        --apple-pay-button-border-radius: ${style?.borderRadius ?? '5px'};
        --apple-pay-button-padding: ${style?.padding ?? '5px 0px'};
        --apple-pay-button-box-sizing: ${style?.boxSizing ?? 'border-box'};
        display: ${buttonRef.current?.shadowRoot ? 'inline-block' : 'none'};
    }
`;

  const createApplePayButton = () =>
    React.createElement('apple-pay-button', {
      buttonstyle: buttonStyle,
      type,
      locale,
      ref: buttonRef,
      class: className ?? undefined
    });

  if (!showApplePayButton) {
    return null;
  }

  return (
    <>
      <style>{createApplePayButtonStyle(style)}</style>
      {createApplePayButton()}
    </>
  );
};

export default ApplePayButton;
